import React from "react";

export default function EditableSectionRenderer({
  field,
  value,
  readOnly,
  onChange
}) {
  const instances = value?.instances || [];
  const childFields = Array.isArray(field.children) && field.children.length > 0
    ? field.children
    : Array.isArray(field.fields)
      ? field.fields
      : [];

  const updateInstanceField = (instanceIndex, fieldId, newValue) => {
    const updated = [...instances];
    // Store in responses object for consistency
    if (!updated[instanceIndex].responses) {
      updated[instanceIndex].responses = {};
    }
    updated[instanceIndex].responses[fieldId] = { value: newValue };
    
    onChange({ instances: updated });
  };

  const addInstance = () => {
    const newInstance = {
      id: crypto.randomUUID ? crypto.randomUUID() : `inst_${Date.now()}`,
      responses: {},
      createdAfterSubmission: true, // Flag for badge
      createdAt: new Date().toISOString()
    };
    
    onChange({ instances: [...instances, newInstance] });
  };

  const removeInstance = (instanceIndex) => {
    const updated = instances.filter((_, i) => i !== instanceIndex);
    onChange({ instances: updated });
  };

  return (
    <div className="section-block border-2 rounded-lg p-5 bg-gray-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-title text-lg font-bold">
          {field.label || field.question}
        </h3>
        {!readOnly && (
          <button
            onClick={addInstance}
            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
          >
            + Add Instance
          </button>
        )}
      </div>

      {instances.length === 0 && (
        <div className="text-sm text-gray-500 italic">
          No entries yet
        </div>
      )}

      {instances.map((instance, idx) => {
        // Handle both old and new data structures
        const instanceResponses = instance.responses || instance.fields || instance;
        
        return (
          <div key={instance.id || idx} className="instance-box border rounded-lg p-4 bg-white mb-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-blue-700">
                  Instance #{idx + 1}
                </div>
                {instance.createdAfterSubmission && (
                  <span className="px-2 py-0.5 bg-orange-200 text-orange-800 text-xs rounded font-medium">
                    Added after submission
                  </span>
                )}
              </div>
              {!readOnly && instances.length > 1 && (
                <button
                  onClick={() => removeInstance(idx)}
                  className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                >
                  Remove
                </button>
              )}
            </div>

            {childFields.map((childField) => {
              const val = instanceResponses[childField.id]?.value ?? instanceResponses[childField.id] ?? "";

              return (
                <div key={childField.id} className="mb-3">
                  <label className="block font-medium text-sm text-gray-700 mb-1">
                    {childField.label || childField.question}
                  </label>

                  {readOnly ? (
                    <div className="answer pl-3 border-l-3 border-gray-200">
                      {renderValue(childField, val)}
                    </div>
                  ) : (
                    renderEditControl(childField, val, (newVal) =>
                      updateInstanceField(idx, childField.id, newVal)
                    )
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function renderEditControl(field, value, onChange) {
  switch (field.type) {
    case "text":
    case "document_number":
      return (
        <input
          type="text"
          className="w-full border rounded px-3 py-2"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "number":
      return (
        <input
          type="number"
          className="w-full border rounded px-3 py-2"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "yesno":
      return (
        <select
          className="w-full border rounded px-3 py-2"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select...</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      );

    case "slider":
      return (
        <div>
          <input
            type="range"
            min={field.config?.min || 0}
            max={field.config?.max || 10}
            step={field.config?.step || 1}
            value={value || 0}
            onChange={(e) => onChange(Number(e.target.value))}
            className="w-full"
          />
          <div className="text-sm text-gray-600 mt-1">Value: {value || 0} / {field.config?.max || 10}</div>
        </div>
      );

    case "checkbox":
      return (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={value === true || value === "true"}
            onChange={(e) => onChange(e.target.checked)}
            className="w-5 h-5"
          />
          <span className="text-sm">Checked</span>
        </label>
      );

    case "date":
    case "date_time":
      return (
        <input
          type="date"
          className="w-full border rounded px-3 py-2"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "multiple_choice":
      return (
        <select
          className="w-full border rounded px-3 py-2"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select...</option>
          {(field.config?.options || []).map((opt, idx) => (
            <option key={idx} value={opt.label || opt}>
              {opt.label || opt}
            </option>
          ))}
        </select>
      );

    default:
      return (
        <input
          type="text"
          className="w-full border rounded px-3 py-2 bg-gray-50"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

function renderValue(field, value) {
  const resolved = value?.value ?? value;
  if (!resolved && resolved !== 0) return <em className="text-gray-400">Not answered</em>;

  switch (field.type) {
    case "yesno":
      return <strong>{resolved === "yes" || resolved === true ? "Yes" : "No"}</strong>;
    case "checkbox":
      return <strong>{resolved ? "✓ Checked" : "☐ Unchecked"}</strong>;
    case "slider":
      return <strong>{resolved} / {field.config?.max || 10}</strong>;
    case "date":
    case "date_time":
      try {
        return new Date(resolved).toLocaleDateString();
      } catch {
        return String(resolved);
      }
    default:
      return <span>{String(resolved)}</span>;
  }
}
